<script setup>
import { toRefs, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHeaderStore } from '@/stores/HeaderStore'
import { useCommonStore } from '@/stores/CommonStore'

const emit = defineEmits(['openSubmenu'])
const props = defineProps({
  links: { type: Array, required: true },
  routerLinkClasses: { type: String, default: ''},
  linkClasses: { type: String, default: ''},
  menuBtnClasses: { type: String, default: '' },
  submenuIdPrefix: { type: String, default: '' }
})

const {
  links,
  routerLinkClasses,
  linkClasses,
  submenuIdPrefix
} = toRefs(props)

const headerStore = useHeaderStore()
const commonStore = useCommonStore()
const router = useRouter()
const route = useRoute()

const currentPath = computed(() => route.path)

const stripQuery = (path) => (path || '').split('?')[0]

const matchesCurrentPath = (path) => {
  const cleanPath = stripQuery(path)
  if (!cleanPath) return false

  return currentPath.value === cleanPath || currentPath.value.startsWith(`${cleanPath}/`)
}

const isLinkSelected = (link) => {
  if (link.subLinks && link.subLinks.length) {
    return link.subLinks.some((subLink) => matchesCurrentPath(subLink.route))
  }

  return matchesCurrentPath(link.route)
}

const getSubmenuId = (index) => `${submenuIdPrefix?.value}-${index}`

const closeSubmenu = (link) => {
  if (commonStore.windowInnerWidth >= 1280) {
    link.openedSublinks = false
  } else {
    headerStore.updateMenuOpenState(false)
  }
}

const closeMenu = () => {
  if (commonStore.windowInnerWidth < 1280) {
    headerStore.updateMenuOpenState(false)
  }
}
</script>

<template>
  <li
    v-for="(link, index) in links"
    :key="index"
    class="link-box"
    :class="{ 'has-sublinks': link.subLinks && link.subLinks.length >= 1 }"
  >
    <template v-if="link.subLinks && link.subLinks.length">
      <button
        type="button"
        class="link-item"
        :class="`${isLinkSelected(link) ? 'selected' : ''} ${routerLinkClasses} ${menuBtnClasses}`"
        :aria-expanded="link.openedSublinks ? 'true' : 'false'"
        :aria-controls="getSubmenuId(index)"
        aria-haspopup="true"
        @click="emit('openSubmenu', router, links, link)"
      >
        <span v-html="link.title" />
        <Icon
          icon="heroicons-solid:chevron-down"
          class="icon"
          :class="{ 'rotate-180': link.openedSublinks }"
        />
      </button>

      <transition name="scale">
        <ul
          v-show="link.openedSublinks"
          :id="getSubmenuId(index)"
          class="submenu-links body-regular !translate-y-1"
        >
          <li
            v-for="(subLink, subIndex) in link.subLinks"
            :key="subIndex"
            @click="closeSubmenu(link)"
          >
            <a
              v-if="subLink.url"
              :href="subLink.url"
              :class="{ 'ignore-selected': subLink.ignoreSelected }"
              class="sub-link"
            >
              {{ subLink.title }}
            </a>

            <router-link
              v-else
              :to="subLink.route"
              :class="{ 'ignore-selected': subLink.ignoreSelected }"
              class="sub-link"
            >
              {{ subLink.title }}
            </router-link>
          </li>
        </ul>
      </transition>
    </template>

    <template v-else>
      <a
        v-if="link.url"
        :href="link.url"
        v-html="link.title"
        class="link-item"
        :class="`${link.isSelected ? 'selected' : ''} ${linkClasses}`"
        @click="closeMenu"
      />
      <router-link
        v-else
        :to="link.route"
        class="link-item"
        :class="`${isLinkSelected(link) ? 'selected' : ''} ${routerLinkClasses}`"
        @click="closeMenu"
      >
        <span v-html="link.title" />
      </router-link>
    </template>
  </li>
</template>

<style scoped>
:deep(.router-link-exact-active.ignore-selected) {
  color: inherit !important;
  font-weight: inherit !important;
  border: none !important;
}
</style>